/**
 * Desktop & Mobile Multi-Touch Input Controller for Aim Assist Training Lab
 * Handles Pointer Lock, WASD kinematics, and touch virtual stick/aiming pads
 */

import * as THREE from 'three';
import { GAME_PRESETS, WEAPON_TYPES } from './constants.js';

export class InputController {
  constructor(canvasElement, camera, trainingEngine, weaponController, settingsStore, audioManager) {
    this.canvas = canvasElement;
    this.camera = camera;
    this.trainingEngine = trainingEngine;
    this.weaponController = weaponController;
    this.settingsStore = settingsStore;
    this.audioManager = audioManager;

    // Enforce standard FPS Euler order (Yaw around world Y, then Pitch around local X)
    if (this.camera) {
      this.camera.rotation.order = 'YXZ';
    }

    // Camera Rotation Angles
    this.pitch = 0; // Look Up / Down
    this.yaw = 0;   // Look Left / Right

    // Recoil Kinematics & Spray Pattern View Punch
    this.viewPunchPitch = 0;
    this.viewPunchYaw = 0;
    this.viewPunchRoll = 0;
    this.continuousShotCount = 0;
    this.lastShotTimestamp = 0;
    this.isFiring = false;
    this.lastMouseSpeedX = 0;

    // Dynamic Game-Specific Recoil Cursor (Crosshair) Movement & Shake
    this.currentCursorOffset = { x: 0, y: 0 };
    this.targetCursorOffset = { x: 0, y: 0 };
    this.cursorShake = { x: 0, y: 0 };
    this.cursorShakeTimer = 0;

    // Player Kinematics
    this.position = new THREE.Vector3(0, 1.65, 0);
    this.velocity = new THREE.Vector3();
    this.moveSpeed = 4.2; // meters/sec
    this.isCrouched = false;
    this.isSprinting = false;
    this.jumpVelocity = 0;
    this.isGrounded = true;

    // Movement Key States
    this.keys = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      jump: false,
      sprint: false,
      crouch: false
    };

    // Mobile Virtual Touch Tracking
    this.touchMoveId = null;
    this.touchMoveOrigin = { x: 0, y: 0 };
    this.touchMoveVector = { x: 0, y: 0 };

    this.touchLookId = null;
    this.lastTouchLookPos = { x: 0, y: 0 };

    // Pointer Lock State
    this.isPointerLocked = false;
    this.justLocked = false;
    this.listeners = [];

    this.init();
  }

  resetKeys() {
    this.keys.forward = false;
    this.keys.backward = false;
    this.keys.left = false;
    this.keys.right = false;
    this.keys.jump = false;
    this.keys.sprint = false;
    this.isFiring = false;
  }

  init() {
    this.setupDesktopControls();
    this.setupPointerLock();
  }

  requestPointerLock() {
    if (this.settingsStore.get('controlMode') === 'desktop' && this.canvas) {
      if (document.pointerLockElement === this.canvas) return;
      try {
        const lockPromise = this.canvas.requestPointerLock?.();
        if (lockPromise && typeof lockPromise.catch === 'function') {
          lockPromise.catch(() => {
            // Silently swallow browser gesture requirement rejections (e.g. gesture token consumed by requestFullscreen)
          });
        }
      } catch (e) {
        // Fallback for older browsers
      }
    }
  }

  setupPointerLock() {
    const handleLockAttempt = () => {
      if ((this.trainingEngine.state === 'active' || this.trainingEngine.state === 'countdown') && !this.isPointerLocked) {
        if (this.settingsStore.get('controlMode') === 'desktop') {
          this.requestPointerLock();
        }
      }
    };

    this.canvas.addEventListener('click', handleLockAttempt);
    const countdownOverlay = document.getElementById('aim-countdown-overlay');
    if (countdownOverlay) {
      countdownOverlay.addEventListener('click', handleLockAttempt);
    }

    const onLockChange = () => {
      this.isPointerLocked = document.pointerLockElement === this.canvas;
      if (this.isPointerLocked) {
        this.justLocked = true; // Guard against browser initial pointer lock displacement spike
      } else {
        this.isFiring = false;
        this.resetKeys();
        if (this.trainingEngine.state === 'active') {
          // Safe auto-pause when user unlocks mouse
          this.trainingEngine.pause();
        }
      }
    };

    const onBlur = () => {
      this.resetKeys();
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('pointerlockchange', onLockChange);
    this.listeners.push(
      () => this.canvas.removeEventListener('click', handleLockAttempt),
      () => countdownOverlay?.removeEventListener('click', handleLockAttempt),
      () => window.removeEventListener('blur', onBlur),
      () => document.removeEventListener('pointerlockchange', onLockChange)
    );
  }

  setupDesktopControls() {
    // 1. Mouse Look with Active Recoil Pull-Down Compensation
    const onMouseMove = (e) => {
      if (!this.isPointerLocked || this.trainingEngine.state !== 'active') return;

      // Discard initial pointer-lock event where browser reports center-warp delta
      if (this.justLocked) {
        this.justLocked = false;
        return;
      }

      // Filter out pointer lock warp anomalies or sudden large spikes
      if (Math.abs(e.movementX) > 280 || Math.abs(e.movementY) > 280) return;

      const sens = Number(this.settingsStore.get('sens')) || 1.0;
      const invertY = this.settingsStore.get('invertY') ? -1 : 1;
      const userAdsMult = Number(this.settingsStore.get('adsMultiplier')) || 0.85;

      // Realistic focal length scaling based on current FOV zoom
      let adsMod = 1.0;
      if (this.weaponController.isAds) {
        const baseFov = Number(this.settingsStore.get('fov')) || 103;
        const currentFov = this.camera ? this.camera.fov : baseFov;
        const zoomRatio = currentFov / baseFov;
        adsMod = userAdsMult * zoomRatio;
      }

      // Sensitivity factor scaled for natural radian rotation
      const sensitivityFactor = 0.0022 * sens * adsMod;
      const deltaYaw = e.movementX * sensitivityFactor;
      const deltaPitch = e.movementY * sensitivityFactor * invertY;

      // Track horizontal velocity for Apex Legends recoil smoothing
      this.lastMouseSpeedX = Math.abs(deltaYaw) / 0.016;

      // Active Recoil Compensation:
      // Pulling mouse DOWN (deltaPitch > 0) actively absorbs upward view punch
      if (deltaPitch > 0 && this.viewPunchPitch > 0) {
        const absorbed = Math.min(deltaPitch, this.viewPunchPitch);
        this.viewPunchPitch -= absorbed;
        const remaining = deltaPitch - absorbed;
        this.pitch -= remaining;
      } else {
        this.pitch -= deltaPitch;
      }

      // Active Cursor Recoil Pull-Down & Counter-Steer Compensation
      // Pulling mouse down physically pulls the climbing cursor back down towards center
      const canvasH = this.canvas?.clientHeight || 600;
      const canvasW = this.canvas?.clientWidth || 1000;
      if (this.currentCursorOffset.y < 0 && deltaPitch > 0) {
        const pullDownPx = deltaPitch * canvasH * 0.70;
        this.currentCursorOffset.y = Math.min(0, this.currentCursorOffset.y + pullDownPx);
        this.targetCursorOffset.y = Math.min(0, this.targetCursorOffset.y + pullDownPx);
      }
      if (deltaYaw !== 0) {
        const steerPx = deltaYaw * canvasW * 0.70;
        if (this.currentCursorOffset.x > 0 && steerPx > 0) {
          this.currentCursorOffset.x = Math.max(0, this.currentCursorOffset.x - steerPx);
          this.targetCursorOffset.x = Math.max(0, this.targetCursorOffset.x - steerPx);
        } else if (this.currentCursorOffset.x < 0 && steerPx < 0) {
          this.currentCursorOffset.x = Math.min(0, this.currentCursorOffset.x - steerPx);
          this.targetCursorOffset.x = Math.min(0, this.targetCursorOffset.x - steerPx);
        }
      }

      this.yaw -= deltaYaw;

      // Clamp vertical pitch to prevent backflips
      this.pitch = Math.max(-Math.PI / 2.05, Math.min(Math.PI / 2.05, this.pitch));
      this.applyCameraRotation();
      this.renderCursor();
    };

    // 2. Mouse Clicks (Left Fire & Spray Holding, Right ADS)
    const onMouseDown = (e) => {
      if (e.target !== this.canvas && !this.canvas.contains(e.target)) return;

      if (e.button === 0) {
        // Left Click: Fire
        this.isFiring = true;
        if (this.trainingEngine.state === 'active') {
          this.triggerFire(performance.now());
        }
      } else if (e.button === 2) {
        // Right Click: ADS
        e.preventDefault();
        this.weaponController.setAds(true);
      }
    };

    const onMouseUp = (e) => {
      if (e.button === 0) {
        this.isFiring = false;
      } else if (e.button === 2) {
        this.weaponController.setAds(false);
      }
    };

    const onContextMenu = (e) => {
      if (this.isPointerLocked || e.target === this.canvas) {
        e.preventDefault(); // Prevent browser context menu on right click
      }
    };

    // 3. Keyboard Input (Full Layout Support: English, Arabic, and Arrows)
    const onKeyDown = (e) => {
      // Don't capture inputs if user is typing in a form input or dialog
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;

      const code = e.code;
      const key = e.key ? e.key.toLowerCase() : '';

      // Movement (WASD + Arabic ص/س/ش/ي + Arrow Keys)
      if (code === 'KeyW' || key === 'w' || key === 'arrowup' || key === 'ص') this.keys.forward = true;
      if (code === 'KeyS' || key === 's' || key === 'arrowdown' || key === 'س') this.keys.backward = true;
      if (code === 'KeyA' || key === 'a' || key === 'arrowleft' || key === 'ش') this.keys.left = true;
      if (code === 'KeyD' || key === 'd' || key === 'arrowright' || key === 'ي') this.keys.right = true;

      // Jump
      if (code === 'Space' || key === ' ') {
        e.preventDefault();
        this.keys.jump = true;
      }
      // Sprint
      if (code === 'ShiftLeft' || code === 'ShiftRight' || key === 'shift') this.keys.sprint = true;
      // Crouch toggle
      if (code === 'KeyC' || key === 'c' || key === 'ؤ') this.keys.crouch = !this.keys.crouch;
      // Reload
      if (code === 'KeyR' || key === 'r' || key === 'ق') this.weaponController.reload();

      // Weapon slots
      if (code === 'Digit1' || key === '1') this.weaponController.switchWeapon('rifle');
      if (code === 'Digit2' || key === '2') this.weaponController.switchWeapon('smg');
      if (code === 'Digit3' || key === '3') this.weaponController.switchWeapon('sniper');
      if (code === 'Digit4' || key === '4') this.weaponController.switchWeapon('pistol');

      if (code === 'Escape' || key === 'escape') {
        if (this.trainingEngine.state === 'active') {
          this.trainingEngine.pause();
        } else if (this.trainingEngine.state === 'paused') {
          this.trainingEngine.resume();
        }
      }
    };

    const onKeyUp = (e) => {
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;

      const code = e.code;
      const key = e.key ? e.key.toLowerCase() : '';

      if (code === 'KeyW' || key === 'w' || key === 'arrowup' || key === 'ص') this.keys.forward = false;
      if (code === 'KeyS' || key === 's' || key === 'arrowdown' || key === 'س') this.keys.backward = false;
      if (code === 'KeyA' || key === 'a' || key === 'arrowleft' || key === 'ش') this.keys.left = false;
      if (code === 'KeyD' || key === 'd' || key === 'arrowright' || key === 'ي') this.keys.right = false;
      if (code === 'Space' || key === ' ') this.keys.jump = false;
      if (code === 'ShiftLeft' || code === 'ShiftRight' || key === 'shift') this.keys.sprint = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    this.listeners.push(
      () => window.removeEventListener('mousemove', onMouseMove),
      () => window.removeEventListener('mousedown', onMouseDown),
      () => window.removeEventListener('mouseup', onMouseUp),
      () => window.removeEventListener('contextmenu', onContextMenu),
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp)
    );
  }

  // ==========================================
  // Mobile Multi-Touch Handlers
  // ==========================================
  handleTouchMoveStart(pointerId, clientX, clientY) {
    this.touchMoveId = pointerId;
    this.touchMoveOrigin = { x: clientX, y: clientY };
    this.touchMoveVector = { x: 0, y: 0 };
  }

  handleTouchMoveUpdate(pointerId, clientX, clientY) {
    if (this.touchMoveId !== pointerId) return;
    const dx = clientX - this.touchMoveOrigin.x;
    const dy = clientY - this.touchMoveOrigin.y;
    const maxDist = 48; // Joystick radius

    const dist = Math.hypot(dx, dy);
    const clampedDist = Math.min(dist, maxDist);
    const angle = Math.atan2(dy, dx);

    this.touchMoveVector = {
      x: (Math.cos(angle) * (clampedDist / maxDist)),
      y: (Math.sin(angle) * (clampedDist / maxDist))
    };
  }

  handleTouchMoveEnd(pointerId) {
    if (this.touchMoveId === pointerId) {
      this.touchMoveId = null;
      this.touchMoveVector = { x: 0, y: 0 };
    }
  }

  handleTouchLookStart(pointerId, clientX, clientY) {
    this.touchLookId = pointerId;
    this.lastTouchLookPos = { x: clientX, y: clientY };
  }

  handleTouchLookUpdate(pointerId, clientX, clientY) {
    if (this.touchLookId !== pointerId || this.trainingEngine.state !== 'active') return;

    const dx = clientX - this.lastTouchLookPos.x;
    const dy = clientY - this.lastTouchLookPos.y;
    this.lastTouchLookPos = { x: clientX, y: clientY };

    const touchSens = Number(this.settingsStore.get('touchSensitivity')) || 1.2;
    const adsMod = this.weaponController.isAds ? 0.75 : 1.0;
    const factor = 0.0035 * touchSens * adsMod;

    this.yaw -= dx * factor;
    this.pitch -= dy * factor;
    this.pitch = Math.max(-Math.PI / 2.1, Math.min(Math.PI / 2.1, this.pitch));
    this.applyCameraRotation();
  }

  handleTouchLookEnd(pointerId) {
    if (this.touchLookId === pointerId) {
      this.touchLookId = null;
    }
  }

  applyCameraRotation() {
    if (!this.camera) return;
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.set(
      this.pitch + this.viewPunchPitch,
      this.yaw + this.viewPunchYaw,
      this.viewPunchRoll,
      'YXZ'
    );
  }

  triggerFire(now = performance.now()) {
    if (this.trainingEngine.state !== 'active') return false;
    const timeSinceLastShot = now - this.lastShotTimestamp;
    const gameId = this.settingsStore.get('selectedGame') || 'valorant';
    const game = GAME_PRESETS[gameId] || GAME_PRESETS.valorant;
    const resetTime = game.recoilResetTimeMs || 320;

    if (timeSinceLastShot > resetTime) {
      this.continuousShotCount = 0;
      this.currentCursorOffset.x = 0;
      this.currentCursorOffset.y = 0;
      this.targetCursorOffset.x = 0;
      this.targetCursorOffset.y = 0;
      this.cursorShake.x = 0;
      this.cursorShake.y = 0;
      this.cursorShakeTimer = 0;
    }
    this.continuousShotCount++;
    this.lastShotTimestamp = now;

    const moveSpeed = this.velocity.length();
    const isCounterStrafing = !this.keys.left && !this.keys.right;
    const isAds = this.weaponController.isAds;
    const weaponConfig = this.weaponController.weaponConfig || WEAPON_TYPES.rifle;

    const canvasH = this.canvas?.clientHeight || 600;
    const canvasW = this.canvas?.clientWidth || 1000;
    const halfH = canvasH * 0.5;
    const halfW = canvasW * 0.5;

    // Compute systematic weapon spray offset (NDC screen angle offset)
    const sprayOffset = this.computeSprayOffset(gameId, weaponConfig, this.continuousShotCount, isAds);

    // Update target cursor displacement in pixels matching selected game spray pattern
    this.targetCursorOffset.x = sprayOffset.x * halfW;
    this.targetCursorOffset.y = -sprayOffset.y * halfH; // Inverted because CSS -Y is UP

    // Snappy responsive impulse: immediate jump of 45% towards the new spray target
    this.currentCursorOffset.x += (this.targetCursorOffset.x - this.currentCursorOffset.x) * 0.45;
    this.currentCursorOffset.y += (this.targetCursorOffset.y - this.currentCursorOffset.y) * 0.45;

    // Apply punchy mechanical gunshot vibration/shake to the cursor based on game settings
    const kickMultiplier = (weaponConfig.recoilPitchPerShot || 0.028) / 0.028;
    const shakePower = (game.recoilPitchBase || 0.02) * kickMultiplier * 600;
    this.cursorShake.x = (Math.random() - 0.5) * (game.recoilYawBase || 0.01) * 700;
    this.cursorShake.y = -(Math.random() * 0.35 + 0.65) * shakePower; // Upward kick
    this.cursorShakeTimer = 0.095; // 95ms mechanical impulse

    // Actual raycast shoots through where the cursor currently is on screen
    const actualSpray = {
      x: (this.currentCursorOffset.x + this.cursorShake.x) / halfW,
      y: -(this.currentCursorOffset.y + this.cursorShake.y) / halfH
    };

    const fired = this.trainingEngine.handlePlayerFire(now, moveSpeed, isCounterStrafing, !this.isGrounded, actualSpray);
    if (fired) {
      this.applyRecoilKick(now, weaponConfig, isAds, sprayOffset);
      this.renderCursor();
      return true;
    }
    return false;
  }

  computeSprayOffset(gameId, weaponConfig, shotIndex, isAds) {
    const game = GAME_PRESETS[gameId] || GAME_PRESETS.valorant;
    const adsDampen = isAds ? 0.70 : 1.0;
    let offsetX = 0;
    let offsetY = 0;

    // Check if the game has a predefined systematic spray curve (Valorant, CS2, Apex, PUBG, Rust, R6, COD)
    if (game.sprayPattern && game.sprayPattern.length > 0) {
      const maxIdx = game.sprayPattern.length - 1;
      const idx = Math.min(Math.max(0, shotIndex - 1), maxIdx);
      const pt = game.sprayPattern[idx];
      // pitch is negative (looking UP in radians) -> in Three.js NDC, UP is positive Y
      offsetY = -pt.pitch * (game.recoilSpread || 1.0) * 1.5;
      offsetX = pt.yaw * (game.recoilSpread || 1.0) * 2.0;

      // When spraying past the defined pattern length, oscillate horizontally
      if (shotIndex > game.sprayPattern.length) {
        const excess = shotIndex - game.sprayPattern.length;
        offsetX += Math.sin(excess * 0.75) * (game.spraySwayAmount || 0.018) * 2.5;
      }
    } else {
      // Dynamic mathematical spray curve scaled by the game's recoil presets
      const basePitch = game.recoilPitchBase || 0.02;
      const baseYaw = game.recoilYawBase || 0.008;
      const climb = Math.min(0.18, basePitch * 1.8 * Math.pow(Math.max(0, shotIndex - 1), 0.72));
      offsetY = climb;
      const sway = Math.sin(shotIndex * 0.7) * (game.spraySwayAmount || baseYaw);
      offsetX = sway * 1.8;
    }

    // Apex Legends Recoil Smoothing: Fast horizontal tracking eliminates horizontal spray
    if (gameId === 'apex' && Math.abs(this.lastMouseSpeedX) > 2.8) {
      offsetX *= 0.15;
    }

    // Weapon type scaling
    if (weaponConfig.modelShape === 'sniper') {
      offsetX = 0;
      offsetY = 0; // Bolt action resets every shot
    } else if (weaponConfig.modelShape === 'smg') {
      offsetY *= 0.85;
      offsetX *= 1.25; // SMGs have faster horizontal flutter
    } else if (weaponConfig.modelShape === 'pistol') {
      offsetY = Math.min(0.065, (game.recoilPitchBase || 0.02) * 1.4 * shotIndex);
      offsetX = (Math.random() - 0.5) * (game.recoilYawBase || 0.01) * 0.8;
    }

    return {
      x: offsetX * adsDampen,
      y: offsetY * adsDampen
    };
  }

  computeRecoilKick(gameId, weaponConfig, shotIndex, isAds) {
    const game = GAME_PRESETS[gameId] || GAME_PRESETS.valorant;
    const basePitch = (game.recoilPitchBase || 0.02) * (weaponConfig.recoilPitchPerShot || 0.028) * 45;
    const adsDampen = isAds ? 0.70 : 1.0;

    let pitchKick = basePitch;

    if (gameId === 'valorant') {
      if (shotIndex <= 2) {
        pitchKick = basePitch * 0.9;
      } else if (shotIndex <= 6) {
        pitchKick = basePitch * 1.45;
      } else {
        pitchKick = basePitch * 0.40;
      }
    } else if (gameId === 'cs2') {
      if (shotIndex <= 2) {
        pitchKick = basePitch * 0.95;
      } else if (shotIndex <= 7) {
        pitchKick = basePitch * 1.40;
      } else {
        pitchKick = basePitch * 0.35;
      }
    } else if (gameId === 'pubg') {
      pitchKick = basePitch * 1.85;
    } else if (gameId === 'cod') {
      pitchKick = basePitch * (shotIndex <= 2 ? 1.6 : 1.0);
    } else {
      pitchKick = basePitch * (1 + Math.min(shotIndex, 10) * 0.04);
    }

    if (weaponConfig.modelShape === 'sniper') {
      pitchKick = (game.recoilPitchBase || 0.02) * 3.8;
    } else if (weaponConfig.modelShape === 'pistol') {
      pitchKick = (game.recoilPitchBase || 0.02) * 1.8;
    }

    return {
      pitchKick: pitchKick * adsDampen,
      yawKick: 0 // Eliminate phantom camera yaw auto-drift
    };
  }

  applyRecoilKick(currentTime, weaponConfig = WEAPON_TYPES.rifle, isAds = false, sprayOffset = null) {
    const gameId = this.settingsStore.get('selectedGame') || 'valorant';
    const { pitchKick } = this.computeRecoilKick(gameId, weaponConfig, this.continuousShotCount, isAds);

    // Apply Camera View Punch (Screen kicks UP on every shot, requiring pull-down)
    this.viewPunchPitch += pitchKick;
    this.viewPunchRoll = (Math.random() - 0.5) * pitchKick * 0.35;

    // Clamp max view punch
    this.viewPunchPitch = Math.min(this.viewPunchPitch, 0.40);

    // Apply Physical Weapon Model Kick, Slide Blowback, and Barrel Tilt
    this.weaponController.applyWeaponShake(pitchKick, 0, isAds, sprayOffset);

    // Update Follow-Recoil Crosshair Offset
    this.updateCrosshairRecoilOffset(sprayOffset);

    this.applyCameraRotation();
  }

  updateCrosshairRecoilOffset(offsetOrX = 0, y = 0) {
    const canvasH = this.canvas?.clientHeight || 600;
    const canvasW = this.canvas?.clientWidth || 1000;
    const halfH = canvasH * 0.5;
    const halfW = canvasW * 0.5;

    if (offsetOrX && typeof offsetOrX === 'object') {
      this.targetCursorOffset.x = (offsetOrX.x || 0) * halfW;
      this.targetCursorOffset.y = -(offsetOrX.y || 0) * halfH;
    } else {
      this.targetCursorOffset.x = (Number(offsetOrX) || 0) * halfW;
      this.targetCursorOffset.y = -(Number(y) || 0) * halfH;
      if (offsetOrX === 0 && y === 0) {
        this.currentCursorOffset.x = 0;
        this.currentCursorOffset.y = 0;
        this.cursorShake.x = 0;
        this.cursorShake.y = 0;
        this.cursorShakeTimer = 0;
      }
    }
    this.renderCursor();
  }

  renderCursor() {
    const crosshairEl = document.getElementById('aim-crosshair');
    if (!crosshairEl) return;

    const followRecoil = this.settingsStore ? this.settingsStore.get('followRecoil') !== false : true;
    if (!followRecoil) {
      crosshairEl.style.transform = 'translate(-50%, -50%)';
      return;
    }

    const shakeX = this.cursorShakeTimer > 0 ? this.cursorShake.x : 0;
    const shakeY = this.cursorShakeTimer > 0 ? this.cursorShake.y : 0;
    const posX = this.currentCursorOffset.x + shakeX;
    const posY = this.currentCursorOffset.y + shakeY;

    crosshairEl.style.transform = `translate(calc(-50% + ${posX.toFixed(1)}px), calc(-50% + ${posY.toFixed(1)}px))`;
  }

  update(delta, timestamp) {
    // 1. Recoil Recovery (Game-Specific Exponential Spring Decay)
    const gameId = this.settingsStore.get('selectedGame') || 'valorant';
    const game = GAME_PRESETS[gameId] || GAME_PRESETS.valorant;
    const recoveryRate = game.recoilRecoveryRate || 8.0;

    if (this.viewPunchPitch > 0) {
      this.viewPunchPitch = Math.max(0, this.viewPunchPitch - delta * recoveryRate * (this.viewPunchPitch * 0.8 + 0.04));
    }
    if (Math.abs(this.viewPunchRoll) > 0.0001) {
      this.viewPunchRoll += (0 - this.viewPunchRoll) * Math.min(delta * 14.0, 1);
    }

    // Dynamic Cursor Recoil Smoothing & Spring Recovery
    const lerpSpeed = this.isFiring ? 30.0 : recoveryRate;
    this.currentCursorOffset.x += (this.targetCursorOffset.x - this.currentCursorOffset.x) * Math.min(delta * lerpSpeed, 1);
    this.currentCursorOffset.y += (this.targetCursorOffset.y - this.currentCursorOffset.y) * Math.min(delta * lerpSpeed, 1);

    // Mechanical Cursor Gunshot Shake Decay
    if (this.cursorShakeTimer > 0) {
      this.cursorShakeTimer -= delta;
      this.cursorShake.x *= Math.max(0, 1 - delta * 26);
      this.cursorShake.y *= Math.max(0, 1 - delta * 26);
    } else {
      this.cursorShake.x = 0;
      this.cursorShake.y = 0;
    }

    const now = timestamp || performance.now();
    if (!this.isFiring && (now - this.lastShotTimestamp > (game.recoilResetTimeMs || 320))) {
      this.continuousShotCount = 0;
      this.targetCursorOffset.x = 0;
      this.targetCursorOffset.y = 0;
    }

    // Full-auto spraying while holding LMB
    if (this.isFiring && this.trainingEngine.state === 'active') {
      if (this.weaponController.canFire(now)) {
        this.triggerFire(now);
      }
    }

    this.applyCameraRotation();
    this.renderCursor();

    // 2. Movement Direction Vector
    const moveDir = new THREE.Vector3();

    // Desktop WASD + Arrow Keys + Arabic Layout
    if (this.keys.forward) moveDir.z -= 1;
    if (this.keys.backward) moveDir.z += 1;
    if (this.keys.left) moveDir.x -= 1;
    if (this.keys.right) moveDir.x += 1;

    // Mobile Joystick
    if (this.touchMoveVector.x !== 0 || this.touchMoveVector.y !== 0) {
      moveDir.x += this.touchMoveVector.x;
      moveDir.z += this.touchMoveVector.y;
    }

    if (moveDir.lengthSq() > 0) {
      moveDir.normalize();
      // Rotate movement vector according to camera yaw
      moveDir.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    }

    // Speed modifiers
    let speed = this.moveSpeed;
    if (this.keys.sprint) speed *= 1.45;
    if (this.keys.crouch) speed *= 0.55;

    this.velocity.copy(moveDir).multiplyScalar(speed);

    // Apply movement position
    this.position.x += this.velocity.x * delta;
    this.position.z += this.velocity.z * delta;

    // Arena boundary clamp (Generous competitive space)
    this.position.x = THREE.MathUtils.clamp(this.position.x, -6.0, 6.0);
    this.position.z = THREE.MathUtils.clamp(this.position.z, -5.0, 4.0);

    // Jump Physics
    if (this.keys.jump && this.isGrounded) {
      this.jumpVelocity = 4.8;
      this.isGrounded = false;
      this.audioManager?.playJump();
    }

    if (!this.isGrounded) {
      this.position.y += this.jumpVelocity * delta;
      this.jumpVelocity -= 14.0 * delta; // Gravity
      const baseHeight = this.keys.crouch ? 1.1 : 1.65;
      if (this.position.y <= baseHeight) {
        this.position.y = baseHeight;
        this.jumpVelocity = 0;
        this.isGrounded = true;
        this.audioManager?.playLand();
      }
    } else {
      this.position.y = this.keys.crouch ? 1.1 : 1.65;
    }

    this.camera.position.copy(this.position);
  }

  reset() {
    this.pitch = 0;
    this.yaw = 0;
    this.viewPunchPitch = 0;
    this.viewPunchYaw = 0;
    this.viewPunchRoll = 0;
    this.continuousShotCount = 0;
    this.isFiring = false;
    this.currentCursorOffset.x = 0;
    this.currentCursorOffset.y = 0;
    this.targetCursorOffset.x = 0;
    this.targetCursorOffset.y = 0;
    this.cursorShake.x = 0;
    this.cursorShake.y = 0;
    this.cursorShakeTimer = 0;
    this.position.set(0, 1.65, 0);
    this.velocity.set(0, 0, 0);
    this.jumpVelocity = 0;
    this.isGrounded = true;
    this.applyCameraRotation();
    this.renderCursor();
  }

  dispose() {
    for (const unbind of this.listeners) unbind();
    this.listeners = [];
  }
}
